import { createClient } from "@supabase/supabase-js";
import {
  observeQueryStart,
  recordRealtimeSubscriptionMetric,
  recordSupabaseCallMetric,
} from "@/lib/queryObservability";

export const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
export const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error("Supabase configuration missing. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in .env.local");
}

const rawSupabase = createClient(supabaseUrl, supabaseAnonKey);

function isDevLikeEnvironment() {
  const mode = String(import.meta.env.MODE ?? "").toLowerCase();
  return mode === "development" || mode === "dev" || mode === "test";
}

const SHOULD_INSTRUMENT = isDevLikeEnvironment();
const WRAPPED = Symbol("instrumented-supabase-builder");

function captureStack(): string[] {
  const stack = new Error().stack ?? "";
  return stack
    .split("\n")
    .slice(2)
    .map((line) => line.trim())
    .filter((line) =>
      line.length > 0 &&
      !line.includes("/src/lib/supabase.ts") &&
      !line.includes("node_modules/@supabase"),
    )
    .slice(0, 8);
}

function stackCaller(stack: string[]): string {
  const firstAppLine = stack.find((line) => line.includes("/src/")) ?? stack[0] ?? "unknown";
  const match = firstAppLine.match(/\/src\/[^)\s]+/);
  return match?.[0] ?? firstAppLine;
}

function describeResult(result: unknown): { status?: number; error?: unknown; payloadBytes: number } {
  const status =
    result && typeof result === "object" && "status" in result && typeof (result as { status?: unknown }).status === "number"
      ? (result as { status: number }).status
      : undefined;
  const error =
    result && typeof result === "object" && "error" in result
      ? (result as { error?: unknown }).error
      : undefined;
  let payloadBytes = 0;
  try {
    payloadBytes = JSON.stringify(result ?? null).length;
  } catch {
    payloadBytes = 0;
  }
  return { status, error, payloadBytes };
}

function logSupabaseCall(input: {
  kind: "from" | "rpc";
  target: string;
  caller: string;
  durationMs: number;
  status?: number;
  error?: unknown;
  stack: string[];
}) {
  recordSupabaseCallMetric(input);
  const hasError = !!input.error;
  const level = hasError ? console.warn : console.info;
  level("[SupabaseTrace]", {
    at: new Date().toISOString(),
    kind: input.kind,
    target: input.target,
    caller: input.caller,
    durationMs: input.durationMs,
    status: input.status,
    error:
      typeof input.error === "string"
        ? input.error
        : input.error && typeof input.error === "object" && "message" in input.error
          ? (input.error as { message?: unknown }).message
          : undefined,
    stack: input.stack,
  });
}

function instrumentBuilder<T extends object>(builder: T, meta: { kind: "from" | "rpc"; target: string; stack: string[] }): T {
  if (!SHOULD_INSTRUMENT || !builder || (builder as Record<PropertyKey, unknown>)[WRAPPED]) {
    return builder;
  }

  const caller = stackCaller(meta.stack);
  const queryKey = `${meta.kind}:${meta.target}:${caller}`;

  return new Proxy(builder, {
    get(target, prop, receiver) {
      if (prop === WRAPPED) return true;
      const value = Reflect.get(target, prop, receiver);

      if (prop === "then" && typeof value === "function") {
        return (onFulfilled?: (value: unknown) => unknown, onRejected?: (reason: unknown) => unknown) => {
          const startedAt = performance.now();
          observeQueryStart(queryKey);
          const wrappedFulfilled = (result: unknown) => {
            const durationMs = performance.now() - startedAt;
            const details = describeResult(result);
            logSupabaseCall({
              kind: meta.kind,
              target: meta.target,
              caller,
              durationMs,
              status: details.status,
              error: details.error,
              stack: meta.stack,
            });
            return onFulfilled ? onFulfilled(result) : result;
          };
          const wrappedRejected = (reason: unknown) => {
            const durationMs = performance.now() - startedAt;
            logSupabaseCall({
              kind: meta.kind,
              target: meta.target,
              caller,
              durationMs,
              error: reason,
              stack: meta.stack,
            });

            const errorCode = typeof reason === "object" && reason && "code" in reason ? (reason as { code?: unknown }).code : undefined;
            if (errorCode === "PGRST301" || errorCode === "bad_jwt") {
              try {
                void supabase.auth.signOut();
              } catch {
                // ignore signOut errors during recovery
              }
            }

            if (onRejected) return onRejected(reason);
            throw reason;
          };
          return value.call(target, wrappedFulfilled, wrappedRejected);
        };
      }

      if (typeof value !== "function") {
        return value;
      }

      return (...args: unknown[]) => {
        const next = value.apply(target, args);
        if (next && (typeof next === "object" || typeof next === "function")) {
          return instrumentBuilder(next as object, meta);
        }
        return next;
      };
    },
  }) as T;
}

function instrumentChannel<T extends { subscribe?: (...args: unknown[]) => unknown; unsubscribe?: (...args: unknown[]) => unknown; topic?: string }>(
  channel: T,
  stack: string[],
): T {
  if (!SHOULD_INSTRUMENT || !channel || (channel as Record<PropertyKey, unknown>)[WRAPPED]) {
    return channel;
  }

  const channelName = String(channel.topic ?? "unknown-channel");
  recordRealtimeSubscriptionMetric({ channel: channelName, event: "created", stack });

  return new Proxy(channel, {
    get(target, prop, receiver) {
      if (prop === WRAPPED) return true;
      const value = Reflect.get(target, prop, receiver);
      if (typeof value !== "function") return value;

      if (prop === "subscribe") {
        return (...args: unknown[]) => {
          const wrappedArgs = [...args];
          const statusIndex = wrappedArgs.findIndex((arg) => typeof arg === "function");
          if (statusIndex >= 0) {
            const callback = wrappedArgs[statusIndex] as (status: unknown, err?: unknown) => unknown;
            wrappedArgs[statusIndex] = (status: unknown, err?: unknown) => {
              recordRealtimeSubscriptionMetric({
                channel: channelName,
                event: "subscribed",
                status: String(status ?? ""),
                stack,
              });
              return callback(status, err);
            };
          } else {
            recordRealtimeSubscriptionMetric({ channel: channelName, event: "subscribed", stack });
          }
          return value.apply(target, wrappedArgs);
        };
      }

      if (prop === "unsubscribe") {
        return (...args: unknown[]) => {
          recordRealtimeSubscriptionMetric({ channel: channelName, event: "unsubscribed", stack });
          return value.apply(target, args);
        };
      }

      return (...args: unknown[]) => {
        const next = value.apply(target, args);
        if (next && (typeof next === "object" || typeof next === "function")) {
          return instrumentChannel(next as T, stack);
        }
        return next;
      };
    },
  }) as T;
}

function createInstrumentedSupabase() {
  if (!SHOULD_INSTRUMENT) return rawSupabase;

  return new Proxy(rawSupabase, {
    get(target, prop, receiver) {
      const value = Reflect.get(target, prop, receiver);
      if (typeof value !== "function") return value;

      if (prop === "from") {
        return (table: string) => {
          const stack = captureStack();
          return instrumentBuilder(value.call(target, table), { kind: "from", target: table, stack });
        };
      }

      if (prop === "rpc") {
        return (fn: string, ...args: unknown[]) => {
          const stack = captureStack();
          return instrumentBuilder(value.call(target, fn, ...args), { kind: "rpc", target: fn, stack });
        };
      }

      if (prop === "channel") {
        return (name: string, ...args: unknown[]) => {
          const stack = captureStack();
          return instrumentChannel(value.call(target, name, ...args), stack);
        };
      }

      if (prop === "removeChannel") {
        return (channel: { topic?: string }, ...args: unknown[]) => {
          recordRealtimeSubscriptionMetric({
            channel: String(channel?.topic ?? "unknown-channel"),
            event: "removed",
            stack: captureStack(),
          });
          return value.call(target, channel, ...args);
        };
      }

      return value.bind(target);
    },
  });
}

export const supabase = createInstrumentedSupabase();

